//******************************************************************************
// import
import { SimpleMeshLayer } from "@deck.gl/mesh-layers";
import { SphereGeometry } from "@luma.gl/engine";

//******************************************************************************
// レイヤIDを決めるためのプレフィックスと値生成関数
const getKey = () => {
	return `spheres_${Date.now() + Math.random()}`;
};

//******************************************************************************
// export class
export class MeasureSpheres {
	//**************************************************************************
	/**
	 * コンストラクタ
	 * @param {{colors:Array<Array<number>>, radius: number}} opts
	*/
	constructor(opts) {
		//----------------------------------------------------------------------
		// 引数をメンバ変数に格納する
		this.opts = opts;
		//----------------------------------------------------------------------
		// optsを整理する
		const options = this.optimizeOpts();
		//----------------------------------------------------------------------
		// propsを構築する
		const props = {
			id: getKey(),
			data: [],
			getPosition: d => d.coordinates,
			getColor: (d, obj) => {
				return obj.index === 0 ? options.colors[0] : options.colors[1];
			},
			pickable: true,
			// material: false,
			mesh: new SphereGeometry({ radius: options.radius }),
		};
		//----------------------------------------------------------------------
		// レイヤを生成してメンバ変数に格納する
		this.layer = new SimpleMeshLayer(props);
	}

	//**************************************************************************
	/**
	 * optsを整理する（一時的にoptsを変更する場合での使用を想定）
	 * @param {{colors:Array<Array<number>>, radius: number}} opts
	 * @returns {{colors:Array<Array<number>>, radius: number}}
	*/
	optimizeOpts(opts) {
		//----------------------------------------------------------------------
		// 元のoptsを引数のoptsで上書きしたoptionsを作る
		const options = {
			...this.opts,
			...(opts || {}),
		};
		//----------------------------------------------------------------------
		// 球の色と半径を決める
		const colors = Array.isArray(options.colors) === false
			? [[255, 0, 0], [255, 0, 0]]
			: options.colors.length < 2
				? [[255, 0, 0], [255, 0, 0]]
				: [options.colors[0], options.colors[1]];
		const radius = typeof options.radius !== "number" ? 10 : options.radius;
		//----------------------------------------------------------------------
		// 整理したoptsを返す
		return { colors, radius };
	}

	//**************************************************************************
	/**
	 * 現状のpropsを復元して返す
	 * @return {Object}
	*/
	restoreProps() {
		//----------------------------------------------------------------------
		// 現状のpropsを取得して新しいpropsを作る
		const props = Object.fromEntries(
			Object.entries(this.layer.props)
		);
		//----------------------------------------------------------------------
		// idを更新しないと新しいデータで描画してくれない
		props.id = getKey();
		//----------------------------------------------------------------------
		// dataとmeshはgetterになっているのであらためてここで取得しておく
		props.data = this.layer.props.data;
		props.mesh = this.layer.props.mesh;
		//----------------------------------------------------------------------
		// 復元したpropsを返す
		return props;
	}

	//**************************************************************************
	/**
	 * 球の更新
	 * @param {[number, number, number]} vertex
	 * @param {number} [index]
	 * @returns {this}
	*/
	update(vertex, index) {
		//----------------------------------------------------------------------
		// 現状のpropsを取得して新しいpropsを作る
		const props = this.restoreProps();
		//----------------------------------------------------------------------
		// indexで分岐
		if (typeof index === "undefined") {
			// 未指定の場合は追加
			props.data.push(vertex);
		} else {
			// 指定の場合は更新
			props.data[index] = vertex;
		}
		//----------------------------------------------------------------------
		// レイヤを再生成
		this.layer = new this.layer.constructor(props);
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * データクリア
	 * @returns {this}
	*/
	clear() {
		//----------------------------------------------------------------------
		// 現状のpropsを取得して新しいpropsを作る
		const props = this.restoreProps();
		//----------------------------------------------------------------------
		// データを空にする
		props.data.length = 0;
		//----------------------------------------------------------------------
		// レイヤを再生成
		this.layer = new this.layer.constructor(props);
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * レイヤを取得
	 * @returns {Object}
	*/
	getLayer() {
		//----------------------------------------------------------------------
		// レイヤインスタンスを返す
		return this.layer;
	}

	//**************************************************************************
	/**
	 * 球のサイズを調整する
	 * @param {number} scale
	 * @returns {this}
	*/
	scaleUpdate(scale) {
		//----------------------------------------------------------------------
		// 現状のpropsを取得して新しいpropsを作る
		const props = this.restoreProps();
		props.getScale = () => {
			return [scale, scale, scale];
		};
		this.layer = new this.layer.constructor(props);
		//----------------------------------------------------------------------
		// 参照を返す
		return this;
	}

	//**************************************************************************
	/**
	 * レイヤ名を取得
	 * @returns {string}
	*/
	getName() {
		//----------------------------------------------------------------------
		// レイヤのコンストラクタ名を返す
		return this.layer.constructor.name;
	}

	//**************************************************************************
	/**
	 * 登録中の球数を返す
	 * @return {number}
	*/
	getCount() {
		//----------------------------------------------------------------------
		// データの長さ（=球の数）を返す
		return this.layer.props.data.length;
	}

	//**************************************************************************
	/**
	 * 登録中の最初の球を返す
	 * @returns {{coordinates:Array<number>, color:Array<number>}}
	*/
	getFirst() {
		//----------------------------------------------------------------------
		// 最初の球を返す
		return this.layer.props.data[0];
	}
};
